import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { FaCheck, FaCopy, FaEnvelope, FaMobileAlt, FaUniversity } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";

const UPI_NOTE_MAX = 60;

function money(value, currency) {
  const n = Number(value || 0);
  const text = n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${text}` : text;
}

function upiPayUrl({ upiId, payee, amount, note }) {
  const params = [
    `pa=${encodeURIComponent(upiId)}`,
    payee ? `pn=${encodeURIComponent(payee)}` : null,
    Number(amount) > 0 ? `am=${Number(amount).toFixed(2)}` : null,
    "cu=INR",
    note ? `tn=${encodeURIComponent(note.slice(0, UPI_NOTE_MAX))}` : null,
  ].filter(Boolean);
  return `upi://pay?${params.join("&")}`;
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
}

function CopyRow({ label, value, copiedKey, onCopy }) {
  if (!value) return null;
  const copied = copiedKey === label;
  return (
    <div className="cportal-pay-row">
      <span className="cportal-pay-row__label">{label}</span>
      <span className="cportal-pay-row__value">{value}</span>
      <button
        type="button"
        className={`cportal-pay-copy${copied ? " is-copied" : ""}`}
        onClick={() => onCopy(label, value)}
        aria-label={`Copy ${label}`}
        title={copied ? "Copied" : `Copy ${label}`}
      >
        {copied ? <FaCheck aria-hidden="true" /> : <FaCopy aria-hidden="true" />}
      </button>
    </div>
  );
}

/**
 * Pay one open invoice (arId) or the whole outstanding balance (arId = null).
 * Shows the company's UPI (QR + app link) and bank-transfer details; nothing is charged here,
 * payments are confirmed by the accounts team.
 */
function PortalPayDialog({ open, arId = null, onClose }) {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accountIdx, setAccountIdx] = useState(0);
  const [method, setMethod] = useState("upi");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [copiedKey, setCopiedKey] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    setOptions(null);
    setAccountIdx(0);
    axiosInstance
      .get("/client/finance/payment-options", { params: arId ? { ar_id: arId } : {} })
      .then((res) => {
        if (active) setOptions(res.data?.data || null);
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.message || "Could not load payment options.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, arId]);

  const accounts = options?.Accounts || [];
  const account = accounts[accountIdx] || null;
  const currency = options?.Currency_Code || "";
  const upiAllowed = !currency || currency === "INR";
  const hasUpi = Boolean(account?.UPI_Id) && upiAllowed && !options?.Mixed_Currency;
  const reference = useMemo(
    () => (options?.Invoices || []).map((i) => i.Invoice_No).filter(Boolean).join(", "),
    [options]
  );

  useEffect(() => {
    setMethod(hasUpi ? "upi" : "bank");
  }, [hasUpi, accountIdx]);

  const upiUrl = hasUpi
    ? upiPayUrl({
        upiId: account.UPI_Id,
        payee: options?.Payee_Name || account.Account_Name,
        amount: options?.Amount,
        note: reference ? `Invoice ${reference}` : "",
      })
    : "";

  useEffect(() => {
    let active = true;
    if (!upiUrl) {
      setQrDataUrl("");
      return undefined;
    }
    QRCode.toDataURL(upiUrl, { width: 220, margin: 1 })
      .then((url) => active && setQrDataUrl(url))
      .catch(() => active && setQrDataUrl(""));
    return () => {
      active = false;
    };
  }, [upiUrl]);

  const handleCopy = async (key, value) => {
    if (await copyText(value)) {
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((k) => (k === key ? "" : k)), 1600);
    }
  };

  if (!open) return null;

  const amountLabel = options?.Mixed_Currency ? null : money(options?.Amount, currency);
  const confirmHref = options?.Payee_Email
    ? `mailto:${options.Payee_Email}?subject=${encodeURIComponent(
        `Payment made${reference ? ` — ${reference}` : ""}`
      )}&body=${encodeURIComponent(
        [
          "Hello,",
          "",
          `We have paid ${amountLabel || "the outstanding amount"}${reference ? ` for invoice ${reference}` : ""}.`,
          "",
          `Paid via: ${method === "upi" ? "UPI" : "Bank transfer"}`,
          "Transaction reference / UTR: ",
          "Payment date: ",
          "",
          "Thank you.",
        ].join("\n")
      )}`
    : null;

  return (
    <div className="cportal-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="cportal-modal cportal-pay-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-pay-title"
      >
        <div className="cportal-modal-header">
          <h3 id="portal-pay-title">{arId ? "Pay invoice" : "Pay outstanding"}</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="cportal-modal-body cportal-pay-body">
          {loading ? (
            <p className="cportal-muted">Loading payment options…</p>
          ) : error ? (
            <p className="cportal-pay-error">{error}</p>
          ) : options ? (
            <>
              <div className="cportal-pay-amount">
                <span className="cportal-pay-amount__label">Amount to pay</span>
                <strong className="cportal-pay-amount__value">
                  {amountLabel || "Multiple currencies"}
                </strong>
                {reference ? (
                  <span className="cportal-pay-amount__ref">
                    {options.Invoices.length === 1 ? "Invoice" : "Invoices"} {reference}
                  </span>
                ) : null}
                {options.Payee_Name ? (
                  <span className="cportal-pay-amount__ref">Pay to {options.Payee_Name}</span>
                ) : null}
              </div>

              {options.Mixed_Currency ? (
                <p className="cportal-pay-hint">
                  Your open invoices are in different currencies. Please pay each invoice separately.
                </p>
              ) : accounts.length === 0 ? (
                <div className="cportal-pay-empty">
                  <p>Online payment details are not set up yet.</p>
                  <p className="cportal-muted">
                    Please contact {options.Payee_Name || "our accounts team"}
                    {options.Payee_Email ? (
                      <>
                        {" "}at <a href={`mailto:${options.Payee_Email}`}>{options.Payee_Email}</a>
                      </>
                    ) : null}
                    {options.Payee_Phone ? <> or {options.Payee_Phone}</> : null} for payment details.
                  </p>
                </div>
              ) : (
                <>
                  {accounts.length > 1 ? (
                    <div className="cportal-pay-accounts" role="tablist" aria-label="Pay to account">
                      {accounts.map((a, i) => (
                        <button
                          key={a.Company_Account_Id}
                          type="button"
                          role="tab"
                          aria-selected={i === accountIdx}
                          className={`cportal-pay-account${i === accountIdx ? " active" : ""}`}
                          onClick={() => setAccountIdx(i)}
                        >
                          {a.Bank_Name || a.Account_Name}
                          {a.Is_Primary ? <span className="cportal-pay-account__tag">Primary</span> : null}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  <div className="cportal-pay-methods" role="tablist" aria-label="Payment method">
                    {hasUpi ? (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={method === "upi"}
                        className={`cportal-pay-method${method === "upi" ? " active" : ""}`}
                        onClick={() => setMethod("upi")}
                      >
                        <FaMobileAlt aria-hidden="true" /> UPI
                      </button>
                    ) : null}
                    <button
                      type="button"
                      role="tab"
                      aria-selected={method === "bank"}
                      className={`cportal-pay-method${method === "bank" ? " active" : ""}`}
                      onClick={() => setMethod("bank")}
                    >
                      <FaUniversity aria-hidden="true" /> Bank transfer
                    </button>
                  </div>

                  {method === "upi" && hasUpi ? (
                    <div className="cportal-pay-upi">
                      {qrDataUrl ? (
                        <img
                          className="cportal-pay-qr"
                          src={qrDataUrl}
                          alt={`UPI QR code to pay ${amountLabel} to ${account.UPI_Id}`}
                        />
                      ) : null}
                      <p className="cportal-pay-hint">Scan with any UPI app (GPay, PhonePe, Paytm, BHIM).</p>
                      <a className="cportal-btn cportal-btn-primary cportal-pay-upi-btn" href={upiUrl}>
                        <FaMobileAlt aria-hidden="true" /> Open UPI app
                      </a>
                      <CopyRow label="UPI ID" value={account.UPI_Id} copiedKey={copiedKey} onCopy={handleCopy} />
                    </div>
                  ) : (
                    <div className="cportal-pay-bank">
                      <CopyRow label="Account name" value={account.Account_Name} copiedKey={copiedKey} onCopy={handleCopy} />
                      <CopyRow label="Account number" value={account.Account_Number} copiedKey={copiedKey} onCopy={handleCopy} />
                      <CopyRow label="IFSC" value={account.IFSC_Code} copiedKey={copiedKey} onCopy={handleCopy} />
                      <CopyRow label="Bank" value={account.Bank_Name} copiedKey={copiedKey} onCopy={handleCopy} />
                      <CopyRow label="Branch" value={account.Branch} copiedKey={copiedKey} onCopy={handleCopy} />
                      <CopyRow label="Account type" value={account.Account_Type} copiedKey={copiedKey} onCopy={handleCopy} />
                      {amountLabel ? (
                        <CopyRow label="Amount" value={Number(options.Amount).toFixed(2)} copiedKey={copiedKey} onCopy={handleCopy} />
                      ) : null}
                    </div>
                  )}

                  {reference ? (
                    <p className="cportal-pay-hint">
                      Please mention <strong>{reference}</strong> in the payment remarks.
                    </p>
                  ) : null}
                  <p className="cportal-pay-hint">
                    Your payment shows here once our accounts team confirms it.
                  </p>
                </>
              )}

              <div className="cportal-form-actions cportal-pay-actions">
                {confirmHref && accounts.length > 0 && !options.Mixed_Currency ? (
                  <a className="cportal-btn cportal-btn-ghost" href={confirmHref}>
                    <FaEnvelope aria-hidden="true" /> I've paid — email confirmation
                  </a>
                ) : null}
                <button type="button" className="cportal-btn cportal-btn-ghost" onClick={onClose}>
                  Close
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default PortalPayDialog;
